import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  IconButton,
} from "@mui/material";
import { BrandButton } from "components/brand";
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  Save,
} from "@mui/icons-material";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./SEOForm.scss";

const T = ADMIN_TEXTS.gallery;

const validationSchema = Yup.object({
  pageTitle: Yup.string().required("Page title is required"),
  metaTitle: Yup.string().required("Meta title is required"),
  metaDescription: Yup.string().required("Meta description is required"),
  ogTitle: Yup.string(),
  ogDescription: Yup.string(),
  canonicalUrl: Yup.string().url("Must be a valid URL"),
  active: Yup.boolean(),
});

export const SEOForm = ({ onSubmit, initialValues = {}, loading = false }) => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    initialValues.ogImage || null,
  );

  const formik = useFormik({
    initialValues: {
      pageTitle: initialValues.pageTitle || "",
      metaTitle: initialValues.metaTitle || "",
      metaDescription: initialValues.metaDescription || "",
      ogTitle: initialValues.ogTitle || "",
      ogDescription: initialValues.ogDescription || "",
      canonicalUrl: initialValues.canonicalUrl || "",
      active: initialValues.active ?? true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit({ ...values, ogImage: imageFile || undefined });
    },
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  useEffect(() => {
    setImagePreview(initialValues.ogImage || null);
  }, [initialValues.ogImage]);

  return (
    <form onSubmit={formik.handleSubmit} className="seo-form">
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={T.pageTitle}
                name="pageTitle"
                value={formik.values.pageTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.pageTitle && Boolean(formik.errors.pageTitle)
                }
                helperText={formik.touched.pageTitle && formik.errors.pageTitle}
                placeholder={T.pageTitlePlaceholder}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={T.metaTitle}
                name="metaTitle"
                value={formik.values.metaTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.metaTitle && Boolean(formik.errors.metaTitle)
                }
                helperText={
                  (formik.touched.metaTitle && formik.errors.metaTitle) ||
                  `${formik.values.metaTitle.length} chars · Best: up to 60`
                }
                placeholder={T.metaTitlePlaceholder}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={T.metaDescription}
                name="metaDescription"
                value={formik.values.metaDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.metaDescription &&
                  Boolean(formik.errors.metaDescription)
                }
                helperText={
                  (formik.touched.metaDescription &&
                    formik.errors.metaDescription) ||
                  `${formik.values.metaDescription.length} chars · Best: up to 160`
                }
                placeholder={T.metaDescriptionPlaceholder}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={T.ogTitle}
                name="ogTitle"
                value={formik.values.ogTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={T.ogTitlePlaceholder}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={T.ogDescription}
                name="ogDescription"
                value={formik.values.ogDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={T.ogDescriptionPlaceholder}
              />
            </Grid>

            <Grid item xs={12}>
              <Box className="image-upload-section">
                <Typography variant="subtitle1" gutterBottom>
                  {T.ogImageLabel}
                </Typography>
                <Paper className="upload-paper">
                  {imagePreview ? (
                    <Box>
                      <Box className="preview-container">
                        <img
                          src={imagePreview}
                          alt="OG Preview"
                          className="preview-image"
                        />
                        <IconButton
                          onClick={handleRemoveImage}
                          size="small"
                          className="remove-button"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <BrandButton
                        variant="secondary"
                        component="label"
                        startIcon={<CloudUpload />}
                      >
                        {T.changeImage}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </BrandButton>
                    </Box>
                  ) : (
                    <Box>
                      <ImageIcon className="upload-icon" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="upload-text"
                      >
                        {T.ogImageRecommendedSize}
                      </Typography>
                      <BrandButton
                        variant="secondary"
                        component="label"
                        startIcon={<CloudUpload />}
                      >
                        {T.uploadImage}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </BrandButton>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={T.canonicalUrl}
                name="canonicalUrl"
                value={formik.values.canonicalUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.canonicalUrl &&
                  Boolean(formik.errors.canonicalUrl)
                }
                helperText={
                  formik.touched.canonicalUrl && formik.errors.canonicalUrl
                }
                placeholder={T.canonicalUrlPlaceholder}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <BrandButton
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                >
                  {loading ? T.saving : ADMIN_TEXTS.common.save}
                </BrandButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  );
};
